import { getRuntimeConfig } from '@apps/web/lib/runtime_config';

export const MAX_RECORDING_DURATION_MS = 30_000;
export const VOICE_INPUT_ERROR_MESSAGE =
    'Couldn’t catch that. Try again or type your message.';
export const VOICE_INPUT_PERMISSION_ERROR_MESSAGE =
    'Microphone access is blocked.';

/** True when getUserMedia failed because the browser (or an embedding
 * permissions policy) refused microphone access, as opposed to a
 * device/recording failure. */
export function isMicrophonePermissionError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'NotAllowedError';
}

// Shared voice-input helpers used by both chat composers.
export function appendTranscribedText(
    current: string,
    transcript: string,
): string {
    const trimmedTranscript = transcript.trim();
    if (!trimmedTranscript) {
        return current;
    }

    if (!current.trim()) {
        return trimmedTranscript;
    }

    return `${current.trimEnd()} ${trimmedTranscript}`;
}

export function getPreferredRecordingMimeType(): string | undefined {
    if (
        typeof MediaRecorder === 'undefined' ||
        typeof MediaRecorder.isTypeSupported !== 'function'
    ) {
        return undefined;
    }

    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        return 'audio/webm;codecs=opus';
    }
    if (MediaRecorder.isTypeSupported('audio/webm')) {
        return 'audio/webm';
    }
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
        return 'audio/mp4';
    }
    return undefined;
}

function getRecordingFileExtension(contentType: string): string {
    if (contentType.includes('mp4')) {
        return 'mp4';
    }
    return 'webm';
}

export async function transcribeAudioBlob({
    audioBlob,
    mode,
    organizationId,
}: {
    readonly audioBlob: Blob;
    readonly mode: string | undefined;
    readonly organizationId: string;
}): Promise<string> {
    const formData = new FormData();
    formData.append(
        'audio',
        audioBlob,
        `voice-input.${getRecordingFileExtension(audioBlob.type)}`,
    );

    const response = await fetch(
        `${getRuntimeConfig().apiUrl}/organizations/${organizationId}/transcribe`,
        {
            method: 'POST',
            credentials: 'include',
            body: formData,
            headers: mode ? { 'X-Mode': mode } : undefined,
        },
    );

    if (!response.ok) {
        throw new Error(`Transcription failed with status ${response.status}`);
    }

    const responseBody: unknown = await response.json();
    if (
        typeof responseBody === 'object' &&
        responseBody !== null &&
        'text' in responseBody &&
        typeof responseBody.text === 'string'
    ) {
        return responseBody.text;
    }

    return '';
}
